import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useFetcher } from "@remix-run/react";
import {
  HomeIcon,
  StarIcon,
  CogIcon,
  PuzzlePieceIcon,
  EnvelopeIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  PaintBrushIcon,
  ShoppingBagIcon,
  Squares2X2Icon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, SVGProps } from "react";
import { isAdminUser } from "~/utils/admin";
import SidebarQuota from "./SidebarQuota";
import { useI18n } from "~/i18n/i18n";
import prizoLogoWhite from "../assets/prizoLoogoWhite.svg";

interface SidebarProps {
  onClose?: () => void;
  userEmail?: string;
  hasAccountIssues?: boolean;
  shopDomain?: string;
}

interface RailItem {
  path: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  admin?: boolean;
}

export default function Sidebar({ onClose, userEmail, hasAccountIssues, shopDomain }: SidebarProps) {
  const location = useLocation();
  const logoutFetcher = useFetcher();
  const { locale, t } = useI18n();
  const isRTL = locale === "he";
  const isAdmin = isAdminUser(userEmail);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the avatar popover on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // tx() returns a translation if the key resolves, otherwise the fallback —
  // keeps Hebrew/RTL working while still allowing short rail labels.
  const tx = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };

  const items: RailItem[] = [
    { path: "/home", label: tx("navigation.home", "Home"), icon: HomeIcon },
    { path: "/", label: tx("navigation.dashboard", "Stats"), icon: ChartBarIcon },
    { path: "/games", label: tx("navigation.games", "Games"), icon: PuzzlePieceIcon },
    { path: "/rewards", label: tx("navigation.rewards", "Prizes"), icon: StarIcon },
    { path: "/emails", label: tx("navigation.emails", "Emails"), icon: EnvelopeIcon },
    { path: "/orders", label: tx("navigation.orders", "Orders"), icon: ShoppingBagIcon },
    { path: "/customize", label: tx("navigation.customize", "Customize"), icon: PaintBrushIcon },
    { path: "/email-templates", label: "Templates", icon: Squares2X2Icon },
    { path: "/integrations", label: "Connect", icon: BoltIcon },
    { path: "/settings", label: tx("navigation.settings", "Settings"), icon: CogIcon },
  ];

  if (isAdmin) {
    items.push({ path: "/backoffice", label: "Admin", icon: ShieldCheckIcon, admin: true });
    items.push({ path: "/email-resend", label: "Resend", icon: ArrowPathIcon, admin: true });
  }

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    if (path === "/home") return location.pathname === "/home";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logoutFetcher.submit({}, { method: "post", action: "/logout" });
    onClose?.();
  };

  const handleLinkClick = () => onClose?.();

  const initial = (userEmail?.trim()?.charAt(0) || "U").toUpperCase();

  return (
    <aside
      className="w-[92px] flex-none h-full flex flex-col items-center py-6 gap-2 text-white"
      style={{ background: "linear-gradient(180deg,#332e7a 0%,#2a265f 100%)" }}
    >
      {/* Logo / mobile close */}
      {onClose ? (
        <button
          onClick={onClose}
          aria-label="Close menu"
          className="flex items-center justify-center w-[42px] h-[42px] rounded-[13px] mb-2 flex-none bg-white/[0.12]"
        >
          <XMarkIcon className="w-5 h-5 text-white" />
        </button>
      ) : (
        <div className="flex items-center justify-center w-[56px] h-[42px] mb-2 flex-none">
          <img src={prizoLogoWhite} alt="Prizo" className="w-full h-full object-contain" />
        </div>
      )}

      {/* Nav rail (scrolls if it overflows) */}
      <nav className="flex flex-col items-center gap-1.5 flex-1 min-h-0 overflow-y-auto w-full px-2 no-scrollbar">
        {items.map((item) => {
          const active = isActive(item.path);
          const Icon = item.icon;
          const showDot = item.path === "/settings" && hasAccountIssues;
          return (
            <Link
              key={item.path}
              to={item.path}
              prefetch="intent"
              onClick={handleLinkClick}
              className={`relative flex flex-col items-center justify-center gap-1.5 w-16 py-[11px] rounded-2xl transition-colors duration-200 ${
                active ? "bg-white text-brand-dark" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              <span className="relative">
                <Icon className="w-[22px] h-[22px]" strokeWidth={1.9} />
                {showDot && (
                  <span className="absolute -top-1 -right-1.5 w-2.5 h-2.5 bg-accent-pink rounded-full border-2 border-[#2a265f]" />
                )}
              </span>
              <span className="text-[9.5px] font-bold tracking-[0.02em] leading-tight text-center">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Avatar + popover (profile / quota / logout) */}
      <div className="relative flex-none mt-2" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Account menu"
          className="w-[42px] h-[42px] rounded-full flex items-center justify-center text-white font-extrabold text-[15px] transition-transform hover:scale-105"
          style={{ background: "linear-gradient(135deg,#ec4899,#f59e0b)" }}
        >
          {initial}
        </button>

        {menuOpen && (
          <div
            className="absolute bottom-0 z-50 w-64 rounded-cardlg bg-white text-ink shadow-lift overflow-hidden left-full ml-3"
          >
            <div className="px-4 pt-4 pb-3">
              <div className="text-[13px] font-extrabold truncate">{userEmail?.split("@")[0] || "User"}</div>
              <div className="text-[12px] text-muted font-semibold truncate">{userEmail || ""}</div>
            </div>
            <SidebarQuota userEmail={userEmail} shopDomain={shopDomain} />
            <button
              onClick={handleLogout}
              disabled={logoutFetcher.state === "submitting"}
              className="w-full flex items-center gap-2.5 px-4 py-3.5 text-[14px] font-bold text-[#ff3e1d] hover:bg-red-50 transition-colors disabled:opacity-50 border-t border-line"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              {logoutFetcher.state === "submitting"
                ? isRTL ? "מתנתק..." : "Logging out..."
                : isRTL ? "התנתק" : "Log Out"}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
